/**
 * Choix d'alertes WhatsApp de « Mon profil »: catégories et heures calmes.
 *
 * Le serveur (`services/openwa_alert_service.py`) range `preferences.whatsapp_alerts`
 * ainsi: `categories` (six booléens) et `heures_calmes` (`debut`, `fin` en
 * « HH:MM »), tous deux facultatifs. Un compte sans `categories` reçoit tout,
 * comme avant. Ce module ne contient que de la logique pure: l'affichage est
 * dans `components/profile/ChoixAlertesWhatsapp.jsx`.
 */

import { libelle } from './libelles';

/** Ordre d'affichage; mêmes codes que `ALERT_CATEGORIES` côté serveur. */
export const CATEGORIES_WHATSAPP = ['trajets', 'manquants', 'documents', 'echeances', 'escalades', 'brief'];

const HEURE = /^([01]\d|2[0-3]):[0-5]\d$/;

const estObjet = (valeur) => Boolean(valeur) && typeof valeur === 'object' && !Array.isArray(valeur);

/** Toutes les catégories à la même valeur. */
export const toutesLesCategories = (valeur) =>
  Object.fromEntries(CATEGORIES_WHATSAPP.map((code) => [code, Boolean(valeur)]));

/**
 * Catégories cochées à l'écran.
 *
 * - choix enregistrés: ceux-ci, une catégorie absente prenant le défaut du rôle
 *   (même règle que le serveur);
 * - abonné d'avant les catégories (WhatsApp actif, aucun choix): tout est
 *   coché, parce que c'est ce qu'il reçoit réellement;
 * - WhatsApp pas encore activé: les défauts de son rôle (`parDefaut`, donnés
 *   par le serveur), sinon tout.
 */
export const categoriesAffichees = ({ stockees, actif, parDefaut } = {}) => {
  const defauts = toutesLesCategories(true);
  if (estObjet(parDefaut)) {
    CATEGORIES_WHATSAPP.forEach((code) => {
      if (typeof parDefaut[code] === 'boolean') defauts[code] = parDefaut[code];
    });
  }
  if (estObjet(stockees)) {
    return Object.fromEntries(
      CATEGORIES_WHATSAPP.map((code) => [code, typeof stockees[code] === 'boolean' ? stockees[code] : defauts[code]]),
    );
  }
  return actif ? toutesLesCategories(true) : defauts;
};

/** Heures calmes enregistrées, en champs de saisie (`''` quand il n'y en a pas). */
export const heuresCalmesAffichees = (stockees) => ({
  debut: estObjet(stockees) && HEURE.test(String(stockees.debut || '')) ? String(stockees.debut) : '',
  fin: estObjet(stockees) && HEURE.test(String(stockees.fin || '')) ? String(stockees.fin) : '',
});

/** Message en français si les heures calmes ne sont pas utilisables, sinon `null`. */
export const verifierHeuresCalmes = (heures) => {
  const debut = String(heures?.debut || '').trim();
  const fin = String(heures?.fin || '').trim();
  if (!debut && !fin) return null;
  if (!debut || !fin || !HEURE.test(debut) || !HEURE.test(fin)) return libelle('choixAlertesWhatsapp', 'calme_incomplet');
  if (debut === fin) return libelle('choixAlertesWhatsapp', 'calme_identique');
  return null;
};

/**
 * Bloc `whatsapp_alerts` à envoyer au serveur: les numéros nettoyés, les six
 * catégories, et les heures calmes seulement si les deux sont remplies.
 */
export const alertesWhatsappPourEnvoi = (alertes) => {
  const envoi = {
    enabled: Boolean(alertes?.enabled),
    numbers: (alertes?.numbers || []).map((numero) => String(numero).trim()).filter(Boolean),
    categories: categoriesAffichees({ stockees: alertes?.categories, actif: true }),
  };
  const debut = String(alertes?.heures_calmes?.debut || '').trim();
  const fin = String(alertes?.heures_calmes?.fin || '').trim();
  if (debut && fin) envoi.heures_calmes = { debut, fin };
  return envoi;
};
